/**
 * Station poses and docking-bay frames (M2 spec §7.2, plan §2.10). Pure maths, no three.js or Rapier.
 *
 * A station is one GLB whose `DOCK_BAY_0` socket sits at the centre of an open-fronted bay box: the socket's
 * local −Z points out through the mouth, local +Y is the bay's up and local +X its right (asset contract §2.5;
 * `docs/art/sheets/m2-placeholders.md`). Its params `bayW`, `bayH`, `bayD` are the box extents along socket X, Y
 * and Z. Everything here is float64 world metres (D-011); nothing is handed to the renderer or the physics.
 *
 * `stationRotation` and `bayFrameFromSocket` run once per station when its model attaches (M2-14), so they may
 * throw on a malformed socket. `pointInBay` and `bayLocal` run every tick and allocate nothing (D-014).
 */
import { AXIS_NEG_Z, AXIS_X, AXIS_Y, QuatScratch, rotateVec, rotateVecFrom, Vec3Scratch, type Quat, type Vec3 } from '../core/math'
import type { SocketEntry } from '../data/assetSchema'
import type { BayFrame } from '../space/contracts'

/** What the bay maths reads from a `DOCK_` socket entry (`ModelEntry.sockets`). */
export type BaySocket = Pick<SocketEntry, 'name' | 'position' | 'quaternion' | 'params'>

/** Below this length a direction is treated as degenerate (parallel axes, zero offsets). */
const DEGENERATE = 1e-9

/** A zeroed bay frame for preallocation (`bayFrameFromSocket` fills it). */
export function createBayFrame(): BayFrame {
  return {
    center: new Vec3Scratch(),
    pad: new Vec3Scratch(),
    mouth: new Vec3Scratch(),
    out: new Vec3Scratch(0, 0, -1),
    up: new Vec3Scratch(0, 1, 0),
    right: new Vec3Scratch(1, 0, 0),
    width: 0,
    height: 0,
    depth: 0,
  }
}

const sq: Quat = new QuatScratch()

/** The socket's out axis (its local −Z) in the station's frame. */
export function socketOut(socket: Pick<SocketEntry, 'quaternion'>, out: Vec3): Vec3 {
  const q = socket.quaternion
  sq.x = q[0]
  sq.y = q[1]
  sq.z = q[2]
  sq.w = q[3]
  return rotateVec(sq, 0, 0, -1, out)
}

/**
 * The station rotation that turns the bay's out axis (`bayLocalOut`, station-local, e.g. `socketOut`) toward
 * `faceToward`, keeping the station's local +Y as level as possible (toward world +Y; world +Z when the
 * approach axis is vertical), then rolled by `roll` radians about the approach axis (positive turns the station's
 * up toward the bay's right as seen along the out axis). Writes into `out` and returns it.
 *
 * Deviation from the plan's `rollDeg`: M2 data converts degrees to radians at load (`StationDef.roll`), so
 * this takes radians like every other runtime angle.
 */
export function stationRotation(
  pos: Readonly<Vec3>,
  faceToward: Readonly<Vec3>,
  roll: number,
  bayLocalOut: Readonly<Vec3>,
  out: Quat,
): Quat {
  // Local basis: out, up (station +Y made perpendicular to out), right = out × up.
  let lox = bayLocalOut.x
  let loy = bayLocalOut.y
  let loz = bayLocalOut.z
  const ll = Math.hypot(lox, loy, loz)
  if (ll < DEGENERATE) throw new Error('stationRotation: the bay out axis is zero')
  lox /= ll
  loy /= ll
  loz /= ll
  const localVertical = Math.abs(loy) > 0.999999
  // Reference up: station +Y, or station +Z when the bay itself points along ±Y.
  const lrefY = localVertical ? 0 : 1
  const lrefZ = localVertical ? 1 : 0
  const ld = lrefY * loy + lrefZ * loz
  let lux = -ld * lox
  let luy = lrefY - ld * loy
  let luz = lrefZ - ld * loz
  const lul = Math.hypot(lux, luy, luz)
  lux /= lul
  luy /= lul
  luz /= lul
  const lrx = loy * luz - loz * luy
  const lry = loz * lux - lox * luz
  const lrz = lox * luy - loy * lux

  // World basis: out toward the target, up level (world +Y, or +Z when vertical), then rolled.
  let wox = faceToward.x - pos.x
  let woy = faceToward.y - pos.y
  let woz = faceToward.z - pos.z
  const wl = Math.hypot(wox, woy, woz)
  if (wl < DEGENERATE) throw new Error('stationRotation: faceToward equals the station position')
  wox /= wl
  woy /= wl
  woz /= wl
  const worldVertical = Math.abs(woy) > 0.999999
  const wrefY = worldVertical ? 0 : 1
  const wrefZ = worldVertical ? 1 : 0
  const wd = wrefY * woy + wrefZ * woz
  let wux = -wd * wox
  let wuy = wrefY - wd * woy
  let wuz = wrefZ - wd * woz
  const wul = Math.hypot(wux, wuy, wuz)
  wux /= wul
  wuy /= wul
  wuz /= wul
  let wrx = woy * wuz - woz * wuy
  let wry = woz * wux - wox * wuz
  let wrz = wox * wuy - woy * wux
  const c = Math.cos(roll)
  const s = Math.sin(roll)
  // Positive roll: up turns toward right (and right toward −up), about the out axis.
  const rux = wux * c + wrx * s
  const ruy = wuy * c + wry * s
  const ruz = wuz * c + wrz * s
  wrx = wrx * c - wux * s
  wry = wry * c - wuy * s
  wrz = wrz * c - wuz * s
  wux = rux
  wuy = ruy
  wuz = ruz

  // R = W · Lᵀ (maps each local basis vector onto its world counterpart).
  const m00 = wox * lox + wux * lux + wrx * lrx
  const m01 = wox * loy + wux * luy + wrx * lry
  const m02 = wox * loz + wux * luz + wrx * lrz
  const m10 = woy * lox + wuy * lux + wry * lrx
  const m11 = woy * loy + wuy * luy + wry * lry
  const m12 = woy * loz + wuy * luz + wry * lrz
  const m20 = woz * lox + wuz * lux + wrz * lrx
  const m21 = woz * loy + wuz * luy + wrz * lry
  const m22 = woz * loz + wuz * luz + wrz * lrz
  return quatFromMatrix(m00, m01, m02, m10, m11, m12, m20, m21, m22, out)
}

/** A row-major 3 × 3 matrix scratch (`mRC`) with its own map, for `quatFromMatrixOf` (P5a11b). */
export class Mat3Scratch {
  m00 = 1
  m01 = 0
  m02 = 0
  m10 = 0
  m11 = 1
  m12 = 0
  m20 = 0
  m21 = 0
  m22 = 1
}

const qm = new Mat3Scratch()

/** Unit quaternion of a rotation matrix (row-major `mRC`), Shepperd's method. */
export function quatFromMatrix(
  m00: number,
  m01: number,
  m02: number,
  m10: number,
  m11: number,
  m12: number,
  m20: number,
  m21: number,
  m22: number,
  out: Quat,
): Quat {
  qm.m00 = m00
  qm.m01 = m01
  qm.m02 = m02
  qm.m10 = m10
  qm.m11 = m11
  qm.m12 = m12
  qm.m20 = m20
  qm.m21 = m21
  qm.m22 = m22
  return quatFromMatrixOf(qm, out)
}

/**
 * `quatFromMatrix` with the matrix read from `m` (the same arithmetic, bit for bit). Only objects cross the call, so
 * the per-tick cradle pin boxes none of the nine floats (P5a11b; D-1052's pattern).
 */
export function quatFromMatrixOf(m: Readonly<Mat3Scratch>, out: Quat): Quat {
  const m00 = m.m00
  const m01 = m.m01
  const m02 = m.m02
  const m10 = m.m10
  const m11 = m.m11
  const m12 = m.m12
  const m20 = m.m20
  const m21 = m.m21
  const m22 = m.m22
  const trace = m00 + m11 + m22
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1)
    out.w = 0.25 / s
    out.x = (m21 - m12) * s
    out.y = (m02 - m20) * s
    out.z = (m10 - m01) * s
  } else if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22)
    out.w = (m21 - m12) / s
    out.x = 0.25 * s
    out.y = (m01 + m10) / s
    out.z = (m02 + m20) / s
  } else if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22)
    out.w = (m02 - m20) / s
    out.x = (m01 + m10) / s
    out.y = 0.25 * s
    out.z = (m12 + m21) / s
  } else {
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11)
    out.w = (m10 - m01) / s
    out.x = (m02 + m20) / s
    out.y = (m12 + m21) / s
    out.z = 0.25 * s
  }
  const l = Math.sqrt(out.x * out.x + out.y * out.y + out.z * out.z + out.w * out.w)
  out.x /= l
  out.y /= l
  out.z /= l
  out.w /= l
  return out
}

function bayParam(socket: BaySocket, key: 'bayW' | 'bayH' | 'bayD'): number {
  const v = socket.params[key]
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) {
    throw new Error(`${socket.name}: param ${key} must be a positive number of metres (got ${String(v)})`)
  }
  return v
}

const tq: Quat = new QuatScratch()
const tv: Vec3 = new Vec3Scratch()
/** The socket position as a vector for `rotateVecFrom` (P5a11b: no floats across the call). */
const sp: Vec3 = new Vec3Scratch()

/**
 * The world bay frame of `socket` on a station at `stationPos` (float64 world) turned by `stationRot`. The pad
 * (where a docked ship's origin rests) is the bay-box centre, directly above the pad disc; the mouth is the
 * centre of the open −Z face. Throws naming the socket when `bayW`, `bayH` or `bayD` is missing.
 */
export function bayFrameFromSocket(stationPos: Readonly<Vec3>, stationRot: Readonly<Quat>, socket: BaySocket, out: BayFrame): BayFrame {
  const width = bayParam(socket, 'bayW')
  const height = bayParam(socket, 'bayH')
  const depth = bayParam(socket, 'bayD')
  const p = socket.position
  sp.x = p[0]
  sp.y = p[1]
  sp.z = p[2]
  rotateVecFrom(stationRot, sp, tv)
  out.center.x = stationPos.x + tv.x
  out.center.y = stationPos.y + tv.y
  out.center.z = stationPos.z + tv.z
  // Socket rotation in world = stationRot ⊗ socketRot.
  const q = socket.quaternion
  const ax = stationRot.x
  const ay = stationRot.y
  const az = stationRot.z
  const aw = stationRot.w
  const bx = q[0]
  const by = q[1]
  const bz = q[2]
  const bw = q[3]
  tq.x = aw * bx + ax * bw + ay * bz - az * by
  tq.y = aw * by - ax * bz + ay * bw + az * bx
  tq.z = aw * bz + ax * by - ay * bx + az * bw
  tq.w = aw * bw - ax * bx - ay * by - az * bz
  const l = Math.sqrt(tq.x * tq.x + tq.y * tq.y + tq.z * tq.z + tq.w * tq.w)
  tq.x /= l
  tq.y /= l
  tq.z /= l
  tq.w /= l
  rotateVecFrom(tq, AXIS_NEG_Z, out.out)
  rotateVecFrom(tq, AXIS_Y, out.up)
  rotateVecFrom(tq, AXIS_X, out.right)
  out.width = width
  out.height = height
  out.depth = depth
  out.pad.x = out.center.x
  out.pad.y = out.center.y
  out.pad.z = out.center.z
  out.mouth.x = out.center.x + out.out.x * depth * 0.5
  out.mouth.y = out.center.y + out.out.y * depth * 0.5
  out.mouth.z = out.center.z + out.out.z * depth * 0.5
  return out
}

/**
 * The world point `p` in bay coordinates relative to the bay-box centre: x along right, y along up, z along out.
 * Allocation-free; only objects cross the call, so a hot caller past V8's inlining budget boxes no floats (DA-1, D-TBD;
 * D-1052's pattern). `out` may alias `p`.
 */
export function bayLocalOf(frame: Readonly<BayFrame>, p: Readonly<Vec3>, out: Vec3): Vec3 {
  const dx = p.x - frame.center.x
  const dy = p.y - frame.center.y
  const dz = p.z - frame.center.z
  out.x = dx * frame.right.x + dy * frame.right.y + dz * frame.right.z
  out.y = dx * frame.up.x + dy * frame.up.y + dz * frame.up.z
  out.z = dx * frame.out.x + dy * frame.out.y + dz * frame.out.z
  return out
}

const at: Vec3 = new Vec3Scratch()

/** `bayLocalOf` for the point (x, y, z) (the same arithmetic, bit for bit). Allocation-free. */
export function bayLocal(frame: Readonly<BayFrame>, x: number, y: number, z: number, out: Vec3): Vec3 {
  at.x = x
  at.y = y
  at.z = z
  return bayLocalOf(frame, at, out)
}

const local: Vec3 = new Vec3Scratch()

/** True when the world point `p` lies inside the bay box (faces included). Allocation-free; objects only (DA-1). */
export function pointInBayOf(frame: Readonly<BayFrame>, p: Readonly<Vec3>): boolean {
  bayLocalOf(frame, p, local)
  return (
    Math.abs(local.x) <= frame.width * 0.5 && Math.abs(local.y) <= frame.height * 0.5 && Math.abs(local.z) <= frame.depth * 0.5
  )
}

/** `pointInBayOf` for the point (x, y, z). Allocation-free. */
export function pointInBay(frame: Readonly<BayFrame>, x: number, y: number, z: number): boolean {
  at.x = x
  at.y = y
  at.z = z
  return pointInBayOf(frame, at)
}
